import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { EmailOtpForm } from '@/components/EmailOtpForm';
import { Button, Card, Icon, type IconName } from '@/design-system';
import { useSession } from '@/lib/store';

const features: { icon: IconName; titleKey: string; bodyKey: string }[] = [
  { icon: 'camera', titleKey: 'landing.feat1Title', bodyKey: 'landing.feat1Body' },
  { icon: 'barcode', titleKey: 'landing.feat2Title', bodyKey: 'landing.feat2Body' },
  { icon: 'print', titleKey: 'landing.feat3Title', bodyKey: 'landing.feat3Body' },
  { icon: 'utensils', titleKey: 'landing.feat4Title', bodyKey: 'landing.feat4Body' },
  { icon: 'spirits', titleKey: 'landing.feat5Title', bodyKey: 'landing.feat5Body' },
  { icon: 'cookie', titleKey: 'landing.feat6Title', bodyKey: 'landing.feat6Body' },
];

const categories: { icon: IconName; label: string }[] = [
  { icon: 'bottle', label: 'Vin' },
  { icon: 'beer', label: 'Øl' },
  { icon: 'spirits', label: 'Sprit' },
  { icon: 'cherry', label: 'Cocktails' },
];

interface Step {
  icon: IconName;
  badge: string;
  title: string;
  body: string;
}

const steps: Step[] = [
  {
    icon: 'camera',
    badge: 'Skann',
    title: 'Pek og fang',
    body:
      'Pek kameraet mot etikett, strekkode eller QR — vin, øl eller sprit. AI henter produsent, type, opphav og årgang på sekunder.',
  },
  {
    icon: 'rack',
    badge: 'Organiser',
    title: 'Plasser i baren',
    body:
      'Dra og slipp flasker mellom hyller, kjøleskap og barvogn. Lag anledninger som "Pizza-fredag" eller "Vorspiel" — så husker Alkisen for deg.',
  },
  {
    icon: 'utensils',
    badge: 'Par',
    title: 'Hva skal du spise?',
    body:
      'Velg kjøtt, fisk, kylling eller vegetar — og få vin-, øl- eller spritforslag fra kjelleren din eller hele Vinmonopolet. Ingen mer gjetting.',
  },
  {
    icon: 'spirits',
    badge: 'Miks',
    title: 'Hva har du på baren?',
    body:
      'Alkisen ser hva du har og foreslår cocktails du kan lage nå — med en kjøpsliste for det som mangler til neste fest.',
  },
  {
    icon: 'users',
    badge: 'Del',
    title: 'Inviter en venn',
    body:
      'Del kjelleren med venner og familie. Du bestemmer hva de får se per scope: alt, bare antall, eller skjult — for hver enkelt.',
  },
];

export function LandingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSession();

  // If already signed in, fast-track to the cellar
  useEffect(() => {
    if (user) navigate('/cellar', { replace: true });
  }, [user, navigate]);

  return (
    <div className="relative isolate min-h-screen overflow-hidden">
      {/* candle glow background */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-32 top-20 h-96 w-96 rounded-full bg-candle/15 blur-3xl animate-flicker" />
        <div className="absolute right-0 top-1/3 h-[28rem] w-[28rem] rounded-full bg-ruby/15 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-oak/30 blur-3xl" />
      </div>

      {/* Top nav */}
      <header className="ui-nav">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Logo size={28} />
          <nav className="hidden items-center gap-6 md:flex">
            <a href="#features" className="text-sm text-mist hover:text-candle">Funksjoner</a>
            <a href="#how" className="text-sm text-mist hover:text-candle">Slik fungerer det</a>
            <a href="#start" className="text-sm text-mist hover:text-candle">Kom i gang</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <button
              type="button"
              onClick={() => i18n.changeLanguage(i18n.language.startsWith('nb') ? 'en' : 'nb')}
              className="ui-pill"
              aria-label="Toggle language"
            >
              <Icon name="globe" size={12} />
              {i18n.language.toUpperCase().slice(0, 2)}
            </button>
            <Link to="/login" className="hidden sm:inline-block">
              <Button variant="ghost" size="sm" iconLeft="login">
                Logg inn
              </Button>
            </Link>
            <a href="#start" className="hidden sm:inline-block">
              <Button variant="primary" size="sm" iconRight="arrow-right">Kom i gang</Button>
            </a>
            <Link to="/login" className="sm:hidden">
              <Button variant="ghost" size="sm" iconLeft="login">Logg inn</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero with inline auth */}
      <section className="mx-auto grid min-h-[80vh] max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-[1.1fr_minmax(320px,420px)]">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="mb-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-line-soft bg-ink-soft/60 px-3 py-1 text-xs uppercase tracking-widest text-candle">
              <Icon name="sparkles" size={12} /> {t('app.motto')}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-candle/40 bg-candle/10 px-3 py-1 text-xs uppercase tracking-widest text-candle">
              <Icon name="sparkles" size={12} /> Powered by AI
            </span>
          </div>
          <h1 className="ui-hero-title mb-6">{t('landing.heroTitle')}</h1>
          <p className="mb-6 max-w-xl text-lg text-parchment-dim">{t('landing.heroSubtitle')}</p>

          {/* Category strip — vin / øl / sprit / cocktails. Icons are central
              to the brand: Alkisen covers everything you'd pour. */}
          <div className="mb-8 flex flex-wrap gap-2">
            {categories.map((c) => (
              <span
                key={c.label}
                className="inline-flex items-center gap-2 rounded-full border border-line-soft bg-ink-soft/60 px-3 py-1.5 text-sm text-parchment-dim"
              >
                <Icon name={c.icon} size={16} className="text-candle" />
                {c.label}
              </span>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-6 max-w-md">
            <Stat number="30k+" label={t('landing.sectionStats1')} />
            <Stat number="4" label={t('landing.sectionStats2')} />
            <Stat number="<1s" label={t('landing.sectionStats3')} />
          </div>
        </motion.div>

        {/* Inline register/login */}
        <motion.div
          id="start"
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Card glow className="grid gap-5 p-6 sm:p-7">
            <div className="grid gap-1 text-center">
              <h2 className="font-serif text-2xl text-parchment">Kom i gang gratis</h2>
              <p className="text-xs text-mist">Skriv inn e-posten din — vi sender en kode. Ingen passord.</p>
            </div>
            <EmailOtpForm variant="compact" />
            <div className="ui-divider" />
            <p className="text-center text-xs text-mist">
              Har du allerede konto?{' '}
              <Link to="/login" className="text-candle hover:underline">
                Logg inn med en kode →
              </Link>
            </p>
          </Card>

          {/* Trust strip — surfaces actually-shipped product affordances */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-[10px] uppercase tracking-widest text-mist">
            <span className="inline-flex items-center gap-1"><Icon name="lock" size={10} /> Ingen passord</span>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1"><Icon name="users" size={10} /> Del med venner</span>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1"><Icon name="sun" size={10} /> Lyst + mørkt</span>
          </div>
        </motion.div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
        <h2 className="mb-2 text-center font-serif text-3xl text-parchment lg:text-4xl">
          {t('landing.sectionFeatures')}
        </h2>
        <div className="mx-auto mb-12 h-px max-w-xs bg-gradient-to-r from-transparent via-candle to-transparent" />
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {features.map((f, idx) => (
            <motion.div
              key={f.titleKey}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ delay: idx * 0.06 }}
              className="ui-card ui-card--interactive p-6"
            >
              <div className="mb-4 grid h-11 w-11 place-items-center rounded-lg bg-candle/10 text-candle">
                <Icon name={f.icon} size={20} />
              </div>
              <h3 className="mb-2 font-serif text-xl text-parchment">{t(f.titleKey)}</h3>
              <p className="text-sm leading-relaxed text-mist">{t(f.bodyKey)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works — five-step flow that mirrors the actual product surfaces. */}
      <section id="how" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
        <h2 className="mb-2 text-center font-serif text-3xl text-parchment lg:text-4xl">
          Slik fungerer Alkisen
        </h2>
        <p className="mx-auto max-w-xl text-center text-sm text-mist">
          Fra første skann til neste skål — fem steg som dekker hele kvelden.
        </p>
        <div className="mx-auto my-12 h-px max-w-xs bg-gradient-to-r from-transparent via-candle to-transparent" />

        {/* Connecting line behind cards on desktop — visual rail that ties the
            five steps together. Hidden on mobile where steps stack vertically. */}
        <div className="relative">
          <div
            aria-hidden
            className="pointer-events-none absolute left-0 right-0 top-7 hidden h-px bg-gradient-to-r from-transparent via-candle/40 to-transparent md:block"
          />
          <div className="relative grid gap-4 md:grid-cols-5">
            {steps.map((s, idx) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ delay: idx * 0.08 }}
                className="ui-card grid gap-3 p-5"
              >
                <div className="flex items-center gap-2">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-candle/15 text-candle ring-1 ring-candle/30">
                    <Icon name={s.icon} size={18} />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-candle">
                    {s.badge}
                  </span>
                  <span className="ml-auto font-serif text-sm text-mist">{`0${idx + 1}`}</span>
                </div>
                <h3 className="font-serif text-lg leading-tight text-parchment">{s.title}</h3>
                <p className="text-sm leading-relaxed text-mist">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="mx-auto max-w-5xl px-4 py-16 text-center sm:px-6">
        <Card glow className="grid gap-5 p-8 sm:p-12">
          <Logo size={40} showWordmark={false} className="justify-self-center" />
          <h2 className="ui-hero-title text-3xl sm:text-4xl">{t('landing.heroTitle')}</h2>
          <p className="mx-auto max-w-md text-mist">{t('landing.heroSubtitle')}</p>
          <div className="mx-auto w-full max-w-sm pt-2">
            <EmailOtpForm variant="compact" />
          </div>
          <p className="text-xs text-mist">
            Allerede medlem?{' '}
            <Link to="/login" className="text-candle hover:underline">Logg inn →</Link>
          </p>
        </Card>
      </section>

      <footer className="border-t border-line py-8">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 text-xs uppercase tracking-widest text-mist sm:px-6">
          <span>{t('landing.footer')}</span>
          <div className="flex items-center gap-4">
            <Link to="/login" className="hover:text-candle">Logg inn</Link>
            <a href="#start" className="hover:text-candle">Kom i gang</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ number, label }: { number: string; label: string }) {
  return (
    <div>
      <div className="font-serif text-3xl font-semibold text-candle">{number}</div>
      <div className="mt-1 text-[10px] uppercase tracking-widest text-mist">{label}</div>
    </div>
  );
}
